// Буфер обмена как источник файлов — пара к isFileDrag (@/lib/filter-files-by-accept):
// drop читает dataTransfer.files, вставка — clipboardData того же типа DataTransfer.

import { blobToPng } from "@/lib/download-image"
import { isAllowedReferenceImageFile } from "@/lib/reference-file-policy"

const EXTENSION_BY_MIME: Record<string, string> = {
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/webp": ".webp",
  "image/heic": ".heic",
  "image/heif": ".heif",
  "video/mp4": ".mp4",
  "video/quicktime": ".mov",
  "audio/mpeg": ".mp3",
  "audio/wav": ".wav",
  "audio/mp4": ".m4a",
}

/** Имя, которое браузер выдаёт вставленной картинке сам — своего смысла не несёт. */
const GENERIC_CLIPBOARD_NAME = /^image\.(png|jpe?g|webp|gif|tiff?|bmp)$/i

/**
 * Вставка считается файловой, только когда в буфере нет осмысленного текста.
 * Текстовые редакторы (Word, таблицы) кладут рядом с картинкой ещё и text/plain,
 * и там пользователь ждёт именно текст; копирование картинки из браузера и файла
 * из Finder text/plain не ставят, поэтому обычный сценарий не страдает.
 */
export function clipboardHasFiles(data: DataTransfer | null): boolean {
  if (!data) return false
  if (data.getData("text/plain").trim().length > 0) return false
  if (data.files.length > 0) return true
  return Array.from(data.items).some((item) => item.kind === "file")
}

function timestampSlug(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  return `${date}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
}

function extensionOf(file: File): string {
  const byMime = EXTENSION_BY_MIME[file.type.toLowerCase()]
  if (byMime) return byMime
  const dot = file.name.lastIndexOf(".")
  return dot < 0 ? "" : file.name.slice(dot).toLowerCase()
}

function renamed(file: File, name: string): File {
  return new File([file], name, { type: file.type, lastModified: file.lastModified })
}

function baseName(name: string): string {
  const dot = name.lastIndexOf(".")
  return dot <= 0 ? name : name.slice(0, dot)
}

/**
 * Файлы из буфера с осмысленным именем.
 *
 * Имя обязательно: часть multipart с пустым filename Rack выбрасывает целиком,
 * и контроллер ответит «файл не передан» вместо ошибки формата. Плюс на Холсте
 * подпись референса — это имя файла (blob.filename), поэтому пачка image.png
 * превратила бы папку доски в стену одинаковых плиток и сделала бы токен
 * @ref[image.png] неоднозначным. Своё имя пользователя (копия файла из Finder)
 * не трогаем.
 */
export function filesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return []
  const raw =
    data.files.length > 0
      ? Array.from(data.files)
      : Array.from(data.items).flatMap((item) => {
          if (item.kind !== "file") return []
          const file = item.getAsFile()
          return file ? [file] : []
        })

  const stamp = timestampSlug(new Date())
  return raw.map((file, index) => {
    const name = file.name.trim()
    if (name.length > 0 && !GENERIC_CLIPBOARD_NAME.test(name)) return file
    const suffix = index === 0 ? "" : `-${index + 1}`
    return renamed(file, `pasted-${stamp}${suffix}${extensionOf(file)}`)
  })
}

/**
 * Safari кладёт в буфер image/tiff, Windows — image/bmp: оба вне allowlist и на
 * фронте, и на бэке. Без конвертации вставка работала бы на Chrome и молча не
 * работала на Safari — худший класс багов, поэтому любое image/* вне списка
 * перегоняем в PNG прямо здесь. Не-картинки отдаём как есть: их разбирает
 * обычный фильтр по режиму.
 */
export async function normalizePastedFile(file: File): Promise<File> {
  if (!file.type.toLowerCase().startsWith("image/")) return file
  if (isAllowedReferenceImageFile(file)) return file
  try {
    const png = await blobToPng(file)
    return new File([png], `${baseName(file.name)}.png`, { type: "image/png" })
  } catch {
    // Не вышло — отдаём исходник: он не пройдёт фильтр и пользователь увидит тост
    // про формат, а не тишину.
    return file
  }
}

export function normalizePastedFiles(files: File[]): Promise<File[]> {
  return Promise.all(files.map(normalizePastedFile))
}
